import React, { useState } from 'react';
import { MarkersAPI } from '../../../api/MarkerEndpoints';
import { ALL_GENRES, ALL_DRESSCODES, ALL_SPECIAL_TYPES, ALL_PRICES } from '../../../constants/EventConstants';
import './Marker-UI.css';
import './ClubCreation-UI.css';
import PageTitle from '../../../components/PageTitle';

const WEEKDAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

const makeEmpty = () => ({
  title: '',
  descriptionGerman: '',
  descriptionEnglish: '',
  selectedGenres: [],
  instagramImageUrl: '',
  instagramName: '',
  address: '',
  entryPrice: '',
  minAge: '',
  dressCode: '',
  drinkPriceIndicator: '',
  latitude: '',
  longitude: '',
  markerType: 'Club',
  specialType: '',
  weekdays: WEEKDAYS.map(d => ({ day: d, open: '', close: '' })),
});

export default function ClubCreation() {
  const [club, setClub] = useState(makeEmpty());
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [success, setSuccess] = useState(null);

  const setField = (name, value) => setClub(prev => ({ ...prev, [name]: value }));

  const toggleChip = (field, value) =>
    setField(field, club[field] === value ? '' : value);

  const toggleGenre = (g) => setField('selectedGenres',
    club.selectedGenres.includes(g)
      ? club.selectedGenres.filter(x => x !== g)
      : [...club.selectedGenres, g]
  );

  const handleWeekdayTime = (index, field, value) =>
    setClub(prev => ({
      ...prev,
      weekdays: prev.weekdays.map((w, i) => i === index ? { ...w, [field]: value } : w),
    }));

  const toggleWeekdayOpen = (index, checked) =>
    setClub(prev => {
      const w = prev.weekdays[index];
      return {
        ...prev,
        weekdays: prev.weekdays.map((it, i) => i === index ? {
          ...it,
          open:  checked ? (w.open  || '22:00') : '',
          close: checked ? (w.close || '04:00') : '',
        } : it),
      };
    });

  const toNum = (v) => (v === '' || v == null ? null : Number(v));

  const isValidInstagramUrl = (url) => {
    if (!url) return true;
    try {
      const u = new URL(url);
      return u.hostname === 'www.instagram.com' || u.hostname === 'instagram.com';
    } catch { return false; }
  };

  const validate = (c) => {
    if (!c.title.trim()) return 'Titel ist erforderlich.';
    if (!isValidInstagramUrl(c.instagramImageUrl)) return 'Bitte eine gültige Instagram-URL eintragen.';
    if (c.latitude && Number.isNaN(toNum(c.latitude))) return 'Latitude muss eine Zahl sein.';
    if (c.longitude && Number.isNaN(toNum(c.longitude))) return 'Longitude muss eine Zahl sein.';
    return null;
  };

  const buildPayload = (c) => ({
    title: c.title,
    descriptionGerman: c.descriptionGerman,
    descriptionEnglish: c.descriptionEnglish,
    musicGenre: c.selectedGenres,
    image: c.instagramImageUrl || '',
    instagramName: c.instagramName || '',
    address: c.address || '',
    entryPrice: toNum(c.entryPrice),
    minAge: toNum(c.minAge),
    dressCode: c.dressCode || '',
    drinkPriceIndicator: toNum(c.drinkPriceIndicator),
    latitude: toNum(c.latitude),
    longitude: toNum(c.longitude),
    markerType: 'Club',
    specialType: c.specialType || null,
    // Only open days: a day sent with empty times would count as open on the map.
    weekdays: c.weekdays
      .filter(w => w.open)
      .map(w => ({ day: w.day, open: w.open, close: w.close || '' })),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    const msg = validate(club);
    if (msg) { setError(msg); return; }
    try {
      setLoading(true);
      await MarkersAPI.createClub(buildPayload(club));
      setSuccess('Club erfolgreich angelegt.');
      setClub(makeEmpty());
    } catch {
      setError('Speichern fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cc-page">
      <div className="page-header">
        <PageTitle title="Club anlegen" description="Einen neuen Club mit Adresse und Öffnungszeiten eintragen. Er erscheint sofort in der App." />
      </div>

      <form className="cc-form-single" onSubmit={handleSubmit}>

        {/* ── Allgemein ── */}
        <div className="cc-card">
          <div className="cc-section cc-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Allgemein
          </div>
          <div className="cc-field">
            <label className="cc-label">Titel *</label>
            <input className="cc-input" value={club.title}
              onChange={e => setField('title', e.target.value)} placeholder="z. B. Berghain Wien" required />
          </div>
          <div className="cc-field">
            <label className="cc-label">Instagram Handle</label>
            <input className="cc-input" value={club.instagramName}
              onChange={e => setField('instagramName', e.target.value)} placeholder="club_handle" />
          </div>
        </div>

        {/* ── Beschreibung ── */}
        <div className="cc-card">
          <div className="cc-section cc-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            Beschreibung
          </div>
          <div className="cc-row">
            <div className="cc-field">
              <label className="cc-label">Deutsch</label>
              <textarea className="cc-textarea" value={club.descriptionGerman}
                onChange={e => setField('descriptionGerman', e.target.value)} placeholder="Kurze Beschreibung auf Deutsch" />
            </div>
            <div className="cc-field">
              <label className="cc-label">English</label>
              <textarea className="cc-textarea" value={club.descriptionEnglish}
                onChange={e => setField('descriptionEnglish', e.target.value)} placeholder="Short description in English" />
            </div>
          </div>
        </div>

        {/* ── Genres ── */}
        <div className="cc-card">
          <div className="cc-section cc-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
            Genres
          </div>
          <div className="mk-chips">
            {ALL_GENRES.map(g => (
              <button key={g} type="button"
                className={`mk-chip${club.selectedGenres.includes(g) ? ' mk-chip--active-green' : ''}`}
                onClick={() => toggleGenre(g)}>
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* ── Titelbild ── */}
        <div className="cc-card">
          <div className="cc-section cc-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            Titelbild
          </div>
          <div className="cc-field">
            <label className="cc-label">Instagram URL</label>
            <input className="cc-input" type="url" value={club.instagramImageUrl}
              onChange={e => setField('instagramImageUrl', e.target.value)}
              placeholder="https://www.instagram.com/p/…" />
          </div>
          {club.instagramImageUrl && (
            <a className="cc-ig-link" href={club.instagramImageUrl} target="_blank" rel="noreferrer">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              Vorschau öffnen
            </a>
          )}
        </div>

        {/* ── Ort ── */}
        <div className="cc-card">
          <div className="cc-section cc-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            Standort
          </div>
          <div className="cc-field">
            <label className="cc-label">Adresse</label>
            <input className="cc-input" value={club.address}
              onChange={e => setField('address', e.target.value)} placeholder="Straße 1, 1010 Wien" />
          </div>
          <div className="cc-row">
            <div className="cc-field">
              <label className="cc-label">Latitude</label>
              <input className="cc-input" inputMode="decimal" value={club.latitude}
                onChange={e => setField('latitude', e.target.value)} placeholder="48.2082" />
            </div>
            <div className="cc-field">
              <label className="cc-label">Longitude</label>
              <input className="cc-input" inputMode="decimal" value={club.longitude}
                onChange={e => setField('longitude', e.target.value)} placeholder="16.3738" />
            </div>
          </div>
        </div>

        {/* ── Preise & Einlass ── */}
        <div className="cc-card">
          <div className="cc-section cc-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            Preise &amp; Einlass
          </div>
          <div className="cc-row">
            <div className="cc-field">
              <label className="cc-label">Eintritt (€)</label>
              <input className="cc-input" inputMode="decimal" value={club.entryPrice}
                onChange={e => setField('entryPrice', e.target.value)} placeholder="z. B. 10" />
            </div>
            <div className="cc-field">
              <label className="cc-label">Mindestalter</label>
              <input className="cc-input" inputMode="numeric" value={club.minAge}
                onChange={e => setField('minAge', e.target.value)} placeholder="z. B. 18" />
            </div>
          </div>
          <div className="cc-field">
            <label className="cc-label">Drink-Preis-Index</label>
            <div className="mk-price-group">
              {ALL_PRICES.map(p => (
                <button key={p} type="button"
                  className={`mk-price-btn${club.drinkPriceIndicator === p ? ' mk-price-btn--active-green' : ''}`}
                  onClick={() => setField('drinkPriceIndicator', club.drinkPriceIndicator === p ? '' : p)}>
                  {'€'.repeat(p)}
                </button>
              ))}
            </div>
          </div>
          <div className="cc-field">
            <label className="cc-label">Dresscode</label>
            <div className="mk-chips">
              {ALL_DRESSCODES.map(d => (
                <button key={d.value} type="button"
                  className={`mk-chip${club.dressCode === d.value ? ' mk-chip--active-green' : ''}`}
                  onClick={() => toggleChip('dressCode', d.value)}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Special Type ── */}
        <div className="cc-card">
          <div className="cc-section cc-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            Special Type
          </div>
          <div className="mk-chips">
            {ALL_SPECIAL_TYPES.map(t => (
              <button key={t.value} type="button"
                className={`mk-chip${club.specialType === t.value ? ' mk-chip--active-green' : ''}`}
                onClick={() => toggleChip('specialType', t.value)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Öffnungszeiten ── */}
        <div className="cc-card">
          <div className="cc-section cc-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Öffnungszeiten
          </div>
          <div className="cc-weekdays">
            {club.weekdays.map((w, index) => {
              const isOpen = Boolean(w.open && w.close);
              return (
                <div key={w.day} className="cc-dayrow">
                  <label className="cc-check">
                    <input type="checkbox" checked={isOpen}
                      onChange={e => toggleWeekdayOpen(index, e.target.checked)} />
                    <span>{w.day}</span>
                  </label>
                  <div className="cc-times" data-disabled={!isOpen}>
                    <input className="cc-time" type="time" value={w.open} disabled={!isOpen}
                      onChange={e => handleWeekdayTime(index, 'open', e.target.value)} />
                    <span className="cc-dash">–</span>
                    <input className="cc-time" type="time" value={w.close} disabled={!isOpen}
                      onChange={e => handleWeekdayTime(index, 'close', e.target.value)} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Submit ── */}
        <div className="cc-actions">
          <button className="cc-btn cc-btn-primary" type="submit" disabled={loading}>
            <svg className="cc-icon-plus" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {loading ? 'Speichern…' : 'Club anlegen'}
          </button>
        </div>

        {error   && <p className="cc-error">{error}</p>}
        {success && <p className="cc-success">{success}</p>}

      </form>
    </div>
  );
}
